import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useServices } from '@/context/ServiceContext'
import { useAuth } from '@/context/AuthContext'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import type { Story } from '@/domain/story'
import type { EscapeRoom, EscapeRoomAttempt } from '@/domain/escapeRoom'
import type { StoryProgress } from '@/domain/story'

const KNOWN_TOPICS = ['phishing', 'sim-swap', 'passwords']

// P4 "mission hub" — the entry point for Interactive Stories and the Escape
// Room (Document 11 §P4 UX requirement: "the learner should feel like
// they're entering a cybersecurity mission," not just clicking another
// dashboard card). Recommendation banner is optional/non-blocking, driven
// by the learner's own weakest quiz topic — never mandatory to proceed.
export function MissionsPage() {
  const { stories, storyProgress, escapeRooms, escapeRoomAttempts, results, episodes } = useServices()
  const { currentUser } = useAuth()

  const [storyList, setStoryList] = useState<Story[]>([])
  const [storyProgressByStoryId, setStoryProgressByStoryId] = useState<Map<string, StoryProgress>>(new Map())
  const [roomList, setRoomList] = useState<EscapeRoom[]>([])
  const [roomAttemptsByRoomId, setRoomAttemptsByRoomId] = useState<Map<string, EscapeRoomAttempt[]>>(new Map())
  const [recommendedTopic, setRecommendedTopic] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!currentUser) return
    let cancelled = false

    async function load() {
      const [allStories, allRooms, myResults, allEpisodes] = await Promise.all([
        stories.list(),
        escapeRooms.list(),
        results.listForUser(currentUser!.id),
        episodes.list(),
      ])

      const storyProgressEntries = await Promise.all(
        allStories.map(async (s) => [s.id, await storyProgress.getProgress(currentUser!.id, s.id)] as const)
      )
      const roomAttemptEntries = await Promise.all(
        allRooms.map(async (r) => [r.id, await escapeRoomAttempts.listAttempts(currentUser!.id, r.id)] as const)
      )

      if (cancelled) return

      setStoryList(allStories)
      setStoryProgressByStoryId(
        new Map(storyProgressEntries.filter(([, p]) => p !== null).map(([id, p]) => [id, p!]))
      )
      setRoomList(allRooms)
      setRoomAttemptsByRoomId(new Map(roomAttemptEntries))

      // Weak-area personalization: the learner's own lowest quiz score,
      // mapped to a canonical topic via the episode's existing topicTags —
      // same technique as buildSarahContext(), not a new calculation.
      const weakest = myResults.reduce<typeof myResults[number] | null>((worst, r) => {
        if (r.passed && r.scorePercent >= 80) return worst
        if (!worst || r.scorePercent < worst.scorePercent) return r
        return worst
      }, null)
      if (weakest) {
        const ep = allEpisodes.find((e) => e.id === weakest.episodeId)
        const topic = ep?.topicTags.find((t) => KNOWN_TOPICS.includes(t))
        setRecommendedTopic(topic ?? null)
      }

      setLoading(false)
    }
    load()
    return () => {
      cancelled = true
    }
  }, [currentUser, stories, storyProgress, escapeRooms, escapeRoomAttempts, results, episodes])

  if (!currentUser) return null
  if (loading) return <LoadingSpinner label="Loading missions…" />

  const recommendedStory = recommendedTopic ? storyList.find((s) => s.topic === recommendedTopic) : null

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
      <span className="cyber-pill bg-indigo-500/15 text-indigo-300 ring-1 ring-indigo-500/30">Missions</span>
      <h1 className="mt-3 font-display text-3xl font-bold text-white">Interactive Missions</h1>
      <p className="mt-2 max-w-2xl text-white/60">
        Step into the story. Every decision has a consequence — and every consequence teaches something.
      </p>

      {recommendedStory && (
        <div className="mt-6 cyber-panel flex flex-col gap-3 border-amber-400/30 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-amber-300">Sarah recommends</p>
            <p className="mt-1 font-display font-semibold text-white">{recommendedStory.title}</p>
            <p className="text-sm text-white/60">Based on your last quiz score, this mission is worth a run.</p>
          </div>
          <Link to={`/stories/${recommendedStory.id}`} className="cyber-btn-primary whitespace-nowrap">
            Start Mission
          </Link>
        </div>
      )}

      <section className="mt-10">
        <h2 className="font-display text-lg font-semibold text-white">Interactive Stories</h2>
        <div className="mt-4 grid gap-5 sm:grid-cols-2">
          {storyList.map((story) => {
            const p = storyProgressByStoryId.get(story.id)
            const status = p?.completed ? (p.outcome === 'good' ? 'Completed — safe outcome' : 'Completed — risky outcome') : p ? 'In progress' : 'Not started'
            return (
              <div key={story.id} className="cyber-panel p-5">
                <p className="font-display font-semibold text-white">{story.title}</p>
                <p className="mt-1 text-sm text-white/60">{story.synopsis}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs text-white/40">{status}</span>
                  <Link to={`/stories/${story.id}`} className="cyber-btn-secondary !px-4 !py-1.5 text-xs">
                    {p ? (p.completed ? 'Replay' : 'Continue') : 'Begin'}
                  </Link>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-lg font-semibold text-white">Cybersecurity Escape Room</h2>
        <div className="mt-4 grid gap-5 sm:grid-cols-2">
          {roomList.map((room) => {
            const attempts = roomAttemptsByRoomId.get(room.id) ?? []
            const bestCompleted = attempts.find((a) => a.status === 'completed')
            const active = attempts.find((a) => a.status === 'in_progress')
            const status = bestCompleted ? 'Escaped ✓' : active ? 'In progress' : attempts.length > 0 ? 'Not yet escaped' : 'Not started'
            return (
              <div key={room.id} className="cyber-panel p-5">
                <p className="font-display font-semibold text-white">{room.title}</p>
                <p className="mt-1 text-sm text-white/60">{room.briefing}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs text-white/40">{status}</span>
                  <Link to={`/escape-rooms/${room.id}`} className="cyber-btn-secondary !px-4 !py-1.5 text-xs">
                    {active ? 'Resume' : bestCompleted ? 'Play Again' : 'Start Mission'}
                  </Link>
                </div>
              </div>
            )
          })}
        </div>
      </section>
    </div>
  )
}
